import { useForm, useFieldArray } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Trash2, X } from 'lucide-react'
import { useMemo } from 'react'

import { Button } from '@/common/ui/button'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import { Textarea } from '@/common/ui/textarea'
import useQueryGetCommission from '@/modules/promotion/api/hooks/useQueryGetCommission'
import useQueryGetReturnLoss from '@/modules/promotion/api/hooks/useQueryGetReturnLoss'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { createRankFormSchema, RankUpRewardLine } from './components'
import { z } from 'zod'
import UploadImage from '@/common/components/UploadImage'
import useMutationCreateRankFrameFileUrl from '@/modules/operator/api/hooks/useMutationCreateRankFrameFileUrl'
import useQueryGetDailyCheckIn from '@/modules/promotion/api/hooks/useQueryGetDailyCheckIn'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import GameSelector from '@/common/components/GameSelector'
import { EXCLUDED_GAME_NAMES } from '@/common/constants/excludedGames'
import { AdminRankUpRewardLineDtoType } from '@/schema'

type RankFormValues = z.infer<typeof createRankFormSchema>

const REWARD_TYPE_OPTIONS: {
	value: AdminRankUpRewardLineDtoType
	label: string
	disabled?: boolean
}[] = [
	{ value: AdminRankUpRewardLineDtoType.DIAMOND, label: 'เพชร' },
	{
		value: AdminRankUpRewardLineDtoType.LUCKY_WHEEL_QUOTA,
		label: 'สิทธิ์หมุนวงล้อ',
	},
	{ value: AdminRankUpRewardLineDtoType.FREE_SPIN, label: 'ฟรีสปิน' },
]

interface RankFormProps {
	defaultValues?: Partial<RankFormValues>
	onSubmit: (data: RankFormValues) => void
	mode: 'create' | 'edit'
}

const preventMinusInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
	if (e.key === '-' || e.key === 'e') {
		e.preventDefault()
	}
}

export default function RankForm({
	onSubmit,
	defaultValues,
	mode,
}: RankFormProps) {
	const navigate = useNavigate()

	const payload = {
		take: 1000,
		skip: 0,
	}

	const { data: commissionCashback } = useQueryGetCommission(payload)
	const { data: returnLoss } = useQueryGetReturnLoss(payload)
	const { data: dailyCheckIn } = useQueryGetDailyCheckIn(payload)
	const { data: presets } = useQueryGetPresetId()
	const { data: gamesData } = useQueryGetGameList({
		providerName: 'PGHARD',
		skip: 0,
		take: 1000,
	})

	const filteredGames = useMemo(() => {
		if (!gamesData?.data) return []
		return gamesData.data.filter(
			(game) => !EXCLUDED_GAME_NAMES.includes(game.name),
		)
	}, [gamesData])

	const { mutateAsync: getRankFrameFileUrl } =
		useMutationCreateRankFrameFileUrl()

	const form = useForm<RankFormValues>({
		resolver: zodResolver(createRankFormSchema),
		defaultValues: {
			name: undefined,
			description: undefined,
			frameUrl: undefined,
			rankProtectionDeposit: undefined,
			rankProtectionTurnover: undefined,
			limitWithdrawCredit: undefined,
			limitWithdrawFrequency: undefined,
			commissionCashbackId: undefined,
			returnLossId: undefined,
			dailyCheckInId: defaultValues?.dailyCheckInId,
			maxExperiencePoints: undefined,
			allowRepeatRankUpRewards: false,
			rankUpRewards: [],
			...defaultValues,
		},
	})

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: 'rankUpRewards',
	})

	const handleSubmit = (data: RankFormValues) => {
		const formattedData = {
			...data,
			frameUrl: data.frameUrl ?? undefined,
			commissionCashbackId: data.commissionCashbackId || null,
			returnLossId: data.returnLossId || null,
			dailyCheckInId: data.dailyCheckInId || null,
		}
		onSubmit(formattedData)
	}

	const usedRewardTypes = form.watch('rankUpRewards')?.map((r) => r.type) ?? []

	const addRewardLine = () => {
		const availableType = REWARD_TYPE_OPTIONS.find(
			(opt) => !usedRewardTypes.includes(opt.value),
		)
		if (!availableType) return

		append({
			type: availableType.value,
			amount: 1,
			luckyWheelSettingId: null,
			gameId: null,
			betAmount: null,
			presetId: null,
		} as RankUpRewardLine)
	}

	const allTypesUsed = usedRewardTypes.length >= REWARD_TYPE_OPTIONS.length

	return (
		<FormProvider {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
				{/* Basic Information Section */}
				<div className="grid grid-cols-2 gap-4">
					{/* Basic Information */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ข้อมูลทั่วไป</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ชื่อแรงค์</FormLabel>
										<FormControl>
											<Input {...field} placeholder="กรอกชื่อแรงค์" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>คำอธิบาย</FormLabel>
										<FormControl>
											<Textarea
												{...field}
												value={field.value ?? ''}
												placeholder="กรอกคำอธิบายแรงค์"
												rows={3}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="maxExperiencePoints"
								render={({ field }) => (
									<FormItem>
										<FormLabel>EXP เพื่อไปแรงค์ถัดไป</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={0}
												onKeyDown={preventMinusInput}
												placeholder="กรอกจำนวนประสบการณ์"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Minimum Requirements */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">เงื่อนไขรักษา rank</h2>
						<div className="grid gap-4">
							<FormField
								control={form.control}
								name="rankProtectionDeposit"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ยอดฝากขั้นต่ำ</FormLabel>
										<FormControl>
											<Input
												type="number"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												onKeyDown={preventMinusInput}
												placeholder="กรอกยอดฝากขั้นต่ำ"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="rankProtectionTurnover"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ยอดเทิร์นขั้นต่ำ (จำนวนเครดิต)</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={0}
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												onKeyDown={preventMinusInput}
												placeholder="กรอกยอดเทิร์นขั้นต่ำ"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				{/* Withdrawal Limits Section */}
				<div className="rounded-lg border p-6">
					<h2 className="mb-6 text-xl font-semibold">ข้อจำกัดการถอน</h2>
					<div className="grid grid-cols-2 gap-4">
						<FormField
							control={form.control}
							name="limitWithdrawCredit"
							render={({ field }) => (
								<FormItem>
									<FormLabel>จำกัดการถอนเครดิต</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											{...field}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำกัดการถอนเครดิต"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="limitWithdrawFrequency"
							render={({ field }) => (
								<FormItem>
									<FormLabel>จำกัดจำนวนครั้งการถอน</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											{...field}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำกัดจำนวนครั้งการถอน"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				{/* Promotions Section */}
				<div className="rounded-lg border p-6">
					<h2 className="mb-6 text-xl font-semibold">โปรโมชั่นที่เกี่ยวข้อง</h2>
					<div className="grid grid-cols-3 gap-4">
						<FormField
							control={form.control}
							name="commissionCashbackId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>คอมมิชชั่น (จากยอดเทิร์น)</FormLabel>
									<div className="flex gap-2">
										<Select
											onValueChange={field.onChange}
											value={field.value ?? undefined}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือก Commission Cashback" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{commissionCashback?.data.map((commission) => (
													<SelectItem key={commission.id} value={commission.id}>
														{commission.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{field.value && (
											<Button
												type="button"
												variant="outline"
												size="icon"
												className="shrink-0"
												onClick={() => field.onChange(null)}
											>
												<X className="h-4 w-4" />
											</Button>
										)}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="returnLossId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>คืนยอดเสีย</FormLabel>
									<div className="flex gap-2">
										<Select
											onValueChange={field.onChange}
											value={field.value ?? undefined}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกคืนยอดเสีย" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{returnLoss?.data.map((loss) => (
													<SelectItem key={loss.id} value={loss.id}>
														{loss.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{field.value && (
											<Button
												type="button"
												variant="outline"
												size="icon"
												className="shrink-0"
												onClick={() => field.onChange(null)}
											>
												<X className="h-4 w-4" />
											</Button>
										)}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="dailyCheckInId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เช็คอินรายวัน</FormLabel>
									<div className="flex gap-2">
										<Select
											onValueChange={field.onChange}
											value={field.value ?? undefined}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกเช็คอินรายวัน" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{dailyCheckIn?.data.map((checkIn) => (
													<SelectItem key={checkIn.id} value={checkIn.id}>
														{checkIn.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{field.value && (
											<Button
												type="button"
												variant="outline"
												size="icon"
												className="shrink-0"
												onClick={() => field.onChange(null)}
											>
												<X className="h-4 w-4" />
											</Button>
										)}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				{/* Rank-Up Rewards Section */}
				<div className="rounded-lg border p-6">
					<div className="mb-6 flex items-center justify-between">
						<h2 className="text-xl font-semibold">รางวัลเลื่อนระดับ</h2>
						<FormField
							control={form.control}
							name="allowRepeatRankUpRewards"
							render={({ field }) => (
								<FormItem className="flex items-center gap-3">
									<FormLabel className="mt-0">
										รับรางวัลซ้ำได้เมื่อเลื่อนระดับซ้ำ
									</FormLabel>
									<FormControl>
										<Switch
											checked={field.value ?? false}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>

					<div className="space-y-3">
						{fields.map((field, index) => {
							const rewardType = form.watch(`rankUpRewards.${index}.type`)
							return (
								<div
									key={field.id}
									className="flex flex-wrap items-start gap-3 rounded-md border p-4"
								>
									{/* Type selector */}
									<FormField
										control={form.control}
										name={`rankUpRewards.${index}.type`}
										render={({ field: f }) => (
											<FormItem className="min-w-[160px] flex-1">
												<FormLabel>ประเภทรางวัล</FormLabel>
												<Select
													onValueChange={(val) => {
														f.onChange(val)
														form.setValue(`rankUpRewards.${index}.gameId`, null)
														form.setValue(
															`rankUpRewards.${index}.presetId`,
															null,
														)
														form.setValue(
															`rankUpRewards.${index}.betAmount`,
															null,
														)
													}}
													value={f.value}
												>
													<FormControl>
														<SelectTrigger>
															<SelectValue placeholder="เลือกประเภท" />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{REWARD_TYPE_OPTIONS.map((opt) => {
															const isUsedByOther =
																usedRewardTypes.includes(opt.value) &&
																opt.value !== f.value
															return (
																<SelectItem
																	key={opt.value}
																	value={opt.value}
																	disabled={isUsedByOther}
																>
																	{opt.label}
																	{isUsedByOther && (
																		<span className="ml-1 text-xs text-muted-foreground">
																			(ใช้แล้ว)
																		</span>
																	)}
																</SelectItem>
															)
														})}
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* Amount */}
									<FormField
										control={form.control}
										name={`rankUpRewards.${index}.amount`}
										render={({ field: f }) => (
											<FormItem className="min-w-[100px] flex-1">
												<FormLabel>จำนวน</FormLabel>
												<FormControl>
													<Input
														type="number"
														min={1}
														onKeyDown={preventMinusInput}
														placeholder="จำนวน"
														value={f.value ?? ''}
														onChange={(e) =>
															f.onChange(
																e.target.value ? Number(e.target.value) : 0,
															)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* FREE_SPIN: Game Selector */}
									{rewardType === 'FREE_SPIN' && (
										<FormField
											control={form.control}
											name={`rankUpRewards.${index}.gameId`}
											render={({ field: f }) => (
												<GameSelector
													field={f}
													games={filteredGames}
													label="เกม (PGHARD เท่านั้น)"
													placeholder="เลือกเกม"
													required
												/>
											)}
										/>
									)}

									{/* FREE_SPIN: RTP Preset */}
									{rewardType === 'FREE_SPIN' && (
										<FormField
											control={form.control}
											name={`rankUpRewards.${index}.presetId`}
											render={({ field: f }) => (
												<FormItem className="min-w-[160px] flex-1">
													<FormLabel required>RTP Preset</FormLabel>
													<Select
														onValueChange={f.onChange}
														value={f.value ?? undefined}
													>
														<FormControl>
															<SelectTrigger>
																<SelectValue placeholder="เลือก RTP Preset" />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{presets?.presets.map((preset) => (
																<SelectItem key={preset.id} value={preset.id}>
																	{preset.note}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}

									{/* FREE_SPIN: betAmount */}
									{rewardType === 'FREE_SPIN' && (
										<FormField
											control={form.control}
											name={`rankUpRewards.${index}.betAmount`}
											render={({ field: f }) => (
												<FormItem className="min-w-[100px] flex-1">
													<FormLabel required>ยอดแทง</FormLabel>
													<FormControl>
														<Input
															type="number"
															min={1}
															onKeyDown={preventMinusInput}
															placeholder="ยอดแทง"
															value={f.value ?? ''}
															onChange={(e) =>
																f.onChange(
																	e.target.value
																		? Number(e.target.value)
																		: null,
																)
															}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}

									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="mt-6 shrink-0 text-destructive hover:text-destructive"
										onClick={() => remove(index)}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</div>
							)
						})}
					</div>

					<Button
						type="button"
						variant="outline"
						size="sm"
						className="mt-4"
						onClick={addRewardLine}
						disabled={allTypesUsed}
					>
						<Plus className="mr-2 h-4 w-4" />
						เพิ่มรางวัล
					</Button>
				</div>

				{/* Media Section */}
				<div className="rounded-lg border p-6">
					<h2 className="mb-6 text-xl font-semibold">รูปภาพ</h2>
					<FormField
						control={form.control}
						name="frameUrl"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Frame URL{' '}
									<span className="text-amber-600 dark:text-amber-400">
										(สัดส่วน 1:1)
									</span>
								</FormLabel>
								<FormControl>
									<UploadImage
										onImageUploaded={(fileUrl) => field.onChange(fileUrl)}
										defaultValue={field.value ?? undefined}
										getFileUrl={() =>
											getRankFrameFileUrl({ payload: { fileExtension: '' } })
										}
										aspectRatio="1/1"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{/* Action Buttons */}
				<div className="flex items-center gap-4 pb-8">
					<Button type="submit" variant="default" className="w-full">
						{mode === 'create' ? 'สร้าง Rank' : 'แก้ไข Rank'}
					</Button>
					<Button
						type="button"
						variant="outline"
						className="w-full"
						onClick={() => navigate(-1)}
					>
						ยกเลิก
					</Button>
				</div>
			</form>
		</FormProvider>
	)
}
